import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import axios from 'axios';
import { toError, assertOk } from '@/services/normalize';
import type { ExtAxiosConfig } from '@/middleware/axios-ui';

import {
  BASE_URL,
  SETTING_IMAGE,
  SETTING_DESCRIPTION,
  EDIT_IMAGE,
  EDIT_THEME,
  THEME_SELECTION,
  UPDATE_THEME,
  GET_WHITE_LABEL,
  LOBBY_CONFIG_HEADER,
  AGENT_CONFIG_HEADER,
  LOBBY_CONFIG_GAMES,
  LOBBY_CONFIG_FOOTER,
  LOBBY_CONFIG_THEME,
  LOBBY_CONFIG_ANNOUNCEMENT,
  LOBBY_CONFIG_BANNERS,
  LOBBY_CONFIG_STORE_IMAGES,
  LOBBY_CONFIG_STORE_IMAGES_DEFAULT,
  LOBBY_CONFIG_LANGUAGE,
  LOBBY_CONFIG_LOADING,
  CORE_URL,
  M_GET,
  M_POST,
  M_PUT
} from '@/utils/api';

function withSuccessToast<T extends Record<string, unknown>>(config: T): T & { __successToast: true } {
  return { ...config, __successToast: true };
}

@Module({ name: 'whitelabel' })
export default class WhiteLabel extends VuexModule {
  list: any = [];

  @Mutation
  mutateSettingImage(whitelabel: any) {
    this.list = whitelabel;
  }

  @Action({ commit: 'mutateSettingImage', rawError: true })
  async FetchSettingImage(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + SETTING_IMAGE,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Fetch setting image failed');

      const _data = data?.data ?? {};
      const game = _data?.image?.game;

      if (game && typeof game === 'object') {
        delete (game as any).loadingBackgroundColor;
        delete (game as any).svgLoading;
        delete (game as any).licence;
        delete (game as any).landscapeLicence;
      }

      if (_data?.image && typeof _data.image === 'object') {
        delete (_data.image as any).landingPage;
      }

      return _data;
    } catch (err: any) {
      throw toError(err, 'Fetch setting image failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigHeader(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_HEADER,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby header config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby header config failed');
    }
  }

  @Action({ rawError: true })
  async GetAgentConfigHeader(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + AGENT_CONFIG_HEADER,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch agent header config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch agent header config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateAgentConfigHeader(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + AGENT_CONFIG_HEADER,
          data: formData,
          method: M_PUT,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );

      assertOk(data, 'Update agent header config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update agent header config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigGames(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_GAMES,
        method: M_GET,
        params
      });

      assertOk(data, 'Fetch lobby games config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby games config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigGames(params: Record<string, any> = {}) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_GAMES,
          data: params,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby games config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby games config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigFooter(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_FOOTER,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby footer config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby footer config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigTheme(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_THEME,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby theme config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby theme config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigTheme(params: Record<string, any> = {}) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_THEME,
          data: params,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby theme config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby theme config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigAnnouncement(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_ANNOUNCEMENT,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby announcement config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby announcement config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigAnnouncement(params: Record<string, any> = {}) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_ANNOUNCEMENT,
          data: params,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby announcement config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby announcement config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigBanners(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_BANNERS,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby banners config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby banners config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigBanners(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_BANNERS,
          data: formData,
          method: M_PUT,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );

      assertOk(data, 'Update lobby banners config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby banners config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigStoreImages(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_STORE_IMAGES,
        method: M_GET,
        params
      });

      assertOk(data, 'Fetch lobby store images config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby store images config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigStoreImages(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_STORE_IMAGES,
          data: formData,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby store images config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby store images config failed');
    }
  }

  @Action({ rawError: true })
  async SetLobbyConfigStoreImagesDefault(params: { key: string }) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_STORE_IMAGES_DEFAULT,
          method: M_GET,
          params: { key: params.key }
        })
      );

      assertOk(data, 'Set lobby store images default failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Set lobby store images default failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigLanguage(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_LANGUAGE,
        method: M_GET,
        params
      });

      assertOk(data, 'Fetch lobby language config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby language config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigLanguage(params: Record<string, any> = {}) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_LANGUAGE,
          data: params,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby language config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby language config failed');
    }
  }

  @Action({ rawError: true })
  async GetLobbyConfigLoading(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + LOBBY_CONFIG_LOADING,
        method: M_GET,
        params,
        ...(params.dt ? { __silentAlert: true } : {})
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch lobby loading config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Fetch lobby loading config failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigLoading(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_LOADING,
          data: formData,
          method: M_PUT
        })
      );

      assertOk(data, 'Update lobby loading config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby loading config failed');
    }
  }

  @Action({ rawError: true })
  async EditSettingDescription(params: Record<string, any> = {}) {
    try {
      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + SETTING_DESCRIPTION,
          data: params,
          method: M_POST
        })
      );

      assertOk(data, 'Edit description failed');
      return data?.status ?? data;
    } catch (err: any) {
      throw toError(err, 'Edit description failed');
    }
  }

  @Action({ rawError: true })
  async EditSettingTheme(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + EDIT_THEME,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Edit theme failed');
      return data?.status ?? data;
    } catch (err: any) {
      throw toError(err, 'Edit theme failed');
    }
  }

  @Action({ rawError: true })
  async UploadImage(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_HEADER,
          data: formData,
          method: M_PUT,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );

      assertOk(data, 'Upload image failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Upload image failed');
    }
  }

  @Action({ rawError: true })
  async UpdateLobbyConfigFooter(params: Record<string, any> | FormData) {
    try {
      const formData =
        params instanceof FormData
          ? params
          : Object.entries(params).reduce((form, [key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                form.append(key, value as string | Blob);
              }
              return form;
            }, new FormData());

      const { data } = await axios(
        withSuccessToast({
          url: BASE_URL + LOBBY_CONFIG_FOOTER,
          data: formData,
          method: M_PUT,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      );
      console.log(formData);
      assertOk(data, 'Update lobby footer config failed');
      return data?.data ?? data;
    } catch (err: any) {
      throw toError(err, 'Update lobby footer config failed');
    }
  }

  @Action({ rawError: true })
  async ImageUpdate(params: Record<string, any> | FormData) {
    try {
      const isFormData = params instanceof FormData;

      const { data } = await axios(
        isFormData
          ? withSuccessToast({
              url: BASE_URL + EDIT_IMAGE,
              data: params,
              method: M_POST,
              headers: { 'Content-Type': 'multipart/form-data' }
            })
          : {
              url: BASE_URL + EDIT_IMAGE,
              data: params,
              method: M_POST
            }
      );

      assertOk(data, 'Update image failed');
      return data?.status ?? data;
    } catch (err: any) {
      throw toError(err, 'Update image failed');
    }
  }

  @Action({ rawError: true })
  async LandingPageSetting(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + SETTING_IMAGE,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Fetch landing page setting failed');
      return data;
    } catch (err: any) {
      throw toError(err, 'Fetch landing page setting failed');
    }
  }

  @Action({ rawError: true })
  async ThemeSelectionSetting(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + THEME_SELECTION,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Fetch theme selection failed');
      return data;
    } catch (err: any) {
      throw toError(err, 'Fetch theme selection failed');
    }
  }

  @Action({ rawError: true })
  async UpdateThemeSelectionSetting(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_THEME,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update theme selection failed');
      return data?.status ?? data;
    } catch (err: any) {
      throw toError(err, 'Update theme selection failed');
    }
  }

  @Action({ rawError: true })
  async FetchWhiteLabel(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: CORE_URL + GET_WHITE_LABEL,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Fetch white label failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch white label failed');
    }
  }
}
